(() => {
  const RECENT_KEY = 'bc:recently-viewed';
  const RECENT_LIMIT = 12;
  const MAX_CARDS = 10;
  const SWYM_TIMEOUT = 6000;
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const handleFromUrl = (url) => {
    const match = String(url || '').match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  };

  try {
    const current = handleFromUrl(window.location.pathname);
    if (current) {
      const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').filter((h) => h !== current);
      list.unshift(current);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_LIMIT)));
    }
  } catch (err) {
  }

  if (customElements.get('cart-empty-carousel')) return;

  const esc = (value) =>
    String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    })[c]);

  const money = (cents) => {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD';
    try {
      return new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(cents / 100);
    } catch (err) {
      return '$' + (cents / 100).toFixed(2);
    }
  };

  const sizedImage = (src, width) => {
    if (!src) return '';
    const url = src.startsWith('//') ? 'https:' + src : src;
    return url + (url.includes('?') ? '&' : '?') + 'width=' + width;
  };

  const productCache = new Map();
  const fetchProduct = (handle) => {
    if (!productCache.has(handle)) {
      productCache.set(
        handle,
        fetch(`${ROOT}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
          .then((res) => (res.ok ? res.json() : null))
          .catch(() => null)
      );
    }
    return productCache.get(handle);
  };

  /*
    Wishlist items come from WishlistStore, the same source the wishlist drawer,
    hearts and PDP button use. wishlist-store.js loads after this file, so wait for it.
    Resolves null if the store never becomes ready, so the Swym fallback can run.
  */
  const fromStore = () =>
    new Promise((resolve) => {
      const started = Date.now();
      let done = false;
      const finish = (items) => {
        if (done) return;
        done = true;
        resolve(items);
      };
      const read = (s) => (s && s.ready ? s.items || [] : null);

      const attach = () => {
        const store = window.WishlistStore;
        if (!store) {
          if (Date.now() - started > SWYM_TIMEOUT) return finish(null);
          return setTimeout(attach, 100);
        }

        const now = read(store.state);
        if (now) return finish(now);

        store.subscribe((s) => {
          const items = read(s);
          if (items) finish(items);
        });
        setTimeout(() => finish(null), SWYM_TIMEOUT);
      };

      attach();
    });

  /* Fallback straight to Swym. The lists API lives on swat.api in this build. */
  const fromSwym = () =>
    new Promise((resolve) => {
      let settled = false;
      const finish = (items) => {
        if (settled) return;
        settled = true;
        resolve(Array.isArray(items) ? items : []);
      };
      setTimeout(() => finish([]), SWYM_TIMEOUT);

      window.SwymCallbacks = window.SwymCallbacks || [];
      window.SwymCallbacks.push((swat) => {
        try {
          const api = swat.api && typeof swat.api.fetchLists === 'function' ? swat.api : swat;
          if (typeof api.fetchLists === 'function') {
            api.fetchLists({
              callbackFn: (lists) => {
                const items = [];
                (lists || []).forEach((list) => items.push(...(list.listcontents || [])));
                finish(items);
              },
              errorFn: () => finish([]),
            });
          } else if (typeof swat.fetch === 'function') {
            swat.fetch((items) => finish(items), () => finish([]));
          } else {
            finish([]);
          }
        } catch (err) {
          finish([]);
        }
      });
    });

  const fetchWishlist = async () => {
    const items = await fromStore();
    return items !== null ? items : fromSwym();
  };

  class CartEmptyCarousel extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded) return;
      this.dataset.loaded = 'true';
      this.load();
    }

    async load() {
      let entries = [];

      if (this.dataset.source === 'wishlist') {
        const items = await fetchWishlist();
        entries = items.map((item) => ({ handle: handleFromUrl(item.du), variantId: Number(item.epi) || null }));
      } else {
        try {
          entries = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').map((handle) => ({ handle, variantId: null }));
        } catch (err) {
          entries = [];
        }
      }

      const seen = new Set();
      entries = entries
        .filter((e) => e.handle && !seen.has(e.handle) && seen.add(e.handle))
        .slice(0, MAX_CARDS);
      if (!entries.length) return;

      const products = await Promise.all(entries.map((e) => fetchProduct(e.handle)));
      const cards = products
        .map((product, i) => (product && product.available ? this.card(product, entries[i].variantId) : ''))
        .filter(Boolean);

      if (!cards.length || !this.isConnected) return;

      this.innerHTML =
        `<h3 class="bc-carousel__heading">${esc(this.dataset.heading)}</h3>` +
        `<ul class="bc-carousel__track" role="list">${cards.join('')}</ul>`;

      const empty = this.closest('.bc-empty');
      if (empty) empty.classList.add('bc-empty--has-carousel');

      this.enableDragScroll(this.querySelector('.bc-carousel__track'));
    }

    enableDragScroll(track) {
      if (!track) return;
      let startX = 0;
      let startScroll = 0;
      let dragging = false;
      let moved = false;

      track.addEventListener('dragstart', (e) => e.preventDefault());

      track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        dragging = true;
        moved = false;
        startX = e.clientX;
        startScroll = track.scrollLeft;
      });

      track.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > 5) {
          moved = true;
          track.classList.add('is-dragging');
          track.setPointerCapture(e.pointerId);
        }
        if (moved) track.scrollLeft = startScroll - dx;
      });

      const end = (e) => {
        if (!dragging) return;
        dragging = false;
        if (moved) {
          track.classList.remove('is-dragging');
          if (track.hasPointerCapture(e.pointerId)) track.releasePointerCapture(e.pointerId);
        }
      };
      track.addEventListener('pointerup', end);
      track.addEventListener('pointercancel', end);

      track.addEventListener(
        'click',
        (e) => {
          if (moved) {
            e.preventDefault();
            e.stopPropagation();
            moved = false;
          }
        },
        true
      );
    }

    card(product, variantId) {
      const variant =
        (variantId && product.variants.find((v) => v.id === variantId && v.available)) ||
        product.variants.find((v) => v.available) ||
        product.variants[0];
      const image = (variant && variant.featured_image && variant.featured_image.src) || product.featured_image;
      const url = product.url || `${ROOT}products/${product.handle}`;
      const price = variant ? variant.price : product.price;

      return `
        <li class="bc-card">
          <a class="bc-card__media" href="${esc(url)}" tabindex="-1" aria-hidden="true">
            ${image ? `<img src="${esc(sizedImage(image, 400))}" alt="" loading="lazy" width="194" height="255">` : ''}
          </a>
          <a class="bc-card__title" href="${esc(url)}">${esc(product.title)}</a>
          <p class="bc-card__price">${money(price)}</p>
        </li>`;
    }
  }

  customElements.define('cart-empty-carousel', CartEmptyCarousel);
})();